import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { formatCurrency, formatDateTime, DEAL_STAGES, PROCUREMENT_SECTORS } from '../lib/utils';
import { 
  Users, Buildings, Handshake, ClipboardText, ChartLineUp, 
  Pulse, ChartLine, Lightning, Shield, FileText, Folder, Megaphone,
  Scales, Database, Warning, CheckCircle, UserCircle, LinkSimple, Briefcase, Truck,
  Bank, IdentificationCard, UsersThree, TrendUp, ShoppingCart,
} from '@phosphor-icons/react';
import AppsOverview from '../components/AppsOverview';
import logger from '../lib/logger';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
    } catch (error) {
      logger.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const dealStageData = stats?.deal_stats ? DEAL_STAGES.map(stage => ({
    name: stage.label,
    value: stats.deal_stats[stage.id]?.value || 0,
    count: stats.deal_stats[stage.id]?.count || 0,
  })).filter(d => d.count > 0) : [];

  const sectorData = stats?.sector_stats ? PROCUREMENT_SECTORS.map(sector => ({
    name: sector.label,
    value: stats.sector_stats[sector.id]?.value || 0,
    count: stats.sector_stats[sector.id]?.count || 0,
  })).filter(d => d.count > 0) : [];

  const COLORS = ['#0047FF', '#10B981', '#F59E0B', '#E11D48', '#8B5CF6', '#06B6D4'];

  const totalDealValue = dealStageData.reduce((sum, d) => sum + d.value, 0);
  const totalDeals = dealStageData.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">
          Welcome to BIL
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Business Intelligence Lounge · Governance · Risk · Compliance
        </p>
      </div>

      {/* SAP Fiori-style tile grid — always shown */}
      <div data-testid="fiori-tiles">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-3">Pages</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <FioriTile color="blue"    icon={Handshake}      title="Deals Pipeline"      subtitle={`${stats?.deals || 0} active`}         onClick={() => navigate('/deals')} />
          <FioriTile color="pink"    icon={Folder}         title="Data Management"     subtitle="Room access & audits"                  onClick={() => navigate('/data-management')} />
          <FioriTile color="orange"  icon={Users}          title="Contacts"            subtitle={`${stats?.contacts || 0} records`}     onClick={() => navigate('/contacts')} />
          <FioriTile color="purple"  icon={Buildings}      title="Companies"           subtitle={`${stats?.companies || 0} entities`}   onClick={() => navigate('/companies')} />
          <FioriTile color="teal"    icon={FileText}       title="Documents"           subtitle="Data rooms & vaults"                   onClick={() => navigate('/documents')} />
          <FioriTile color="red"     icon={Scales}         title="Compliance"          subtitle="Audit & regulations"                   onClick={() => navigate('/audit-trail')} />
          <FioriTile color="slate"   icon={Shield}         title="Risk & Controls"     subtitle="Access control & RBAC"                 onClick={() => navigate('/access-control')} />
          <FioriTile color="green"   icon={CheckCircle}    title="Tasks"               subtitle={`${stats?.tasks_pending || 0} pending`} onClick={() => navigate('/tasks')} />
          <FioriTile color="indigo"  icon={UserCircle}     title="Client Portal"       subtitle="External access"                       onClick={() => navigate('/client-portal')} />
          <FioriTile color="amber"   icon={LinkSimple}     title="Share Links"         subtitle="Monitor & revoke"                      onClick={() => navigate('/share-links')} />
          <FioriTile color="purple"  icon={Briefcase}      title="Projects"            subtitle="Plan & deliver"                        onClick={() => navigate('/projects')} />
          <FioriTile color="orange"  icon={Truck}          title="Supply Chain"        subtitle="Suppliers & inventory"                 onClick={() => navigate('/supply-chain')} />
          <FioriTile color="grey"    icon={ShoppingCart}   title="Procurement"         subtitle="Sourcing & RFQs"                       onClick={() => navigate('/procurement')} />
          <FioriTile color="magenta" icon={Bank}           title="Finance"             subtitle="Invoices & ledgers"                    onClick={() => navigate('/finance')} />
          <FioriTile color="green"   icon={TrendUp}        title="Invest"              subtitle="Buy & sell businesses"                 onClick={() => navigate('/invest')} />
          <FioriTile color="rose"    icon={IdentificationCard} title="Human Resources" subtitle="Employees & payroll"                  onClick={() => navigate('/hr')} />
          <FioriTile color="sky"     icon={UsersThree}     title="Teams"               subtitle="Cross-functional squads"               onClick={() => navigate('/teams')} />
        </div>
      </div>

      {/* SAP-style Apps section: Favorites / Most Used / Recent / Recommended */}
      <AppsOverview />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Contacts"
          value={stats?.contacts || 0}
          testId="stat-contacts"
        />
        <StatCard
          icon={Buildings}
          label="Companies"
          value={stats?.companies || 0}
          testId="stat-companies"
        />
        <StatCard
          icon={Handshake}
          label="Active Deals"
          value={stats?.deals || 0}
          testId="stat-deals"
        />
        <StatCard
          icon={ClipboardText}
          label="Pending Tasks"
          value={stats?.tasks_pending || 0}
          testId="stat-tasks"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deal Pipeline Value */}
        <div className="bg-card border border-border p-6 rounded-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Pipeline Value</h3>
              <p className="text-2xl font-bold text-primary">{formatCurrency(totalDealValue)}</p>
            </div>
            <ChartLine size={24} className="text-muted-foreground" />
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dealStageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '2px'
                  }}
                />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Deals by Sector */}
        <div className="bg-card border border-border p-6 rounded-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Deals by Sector</h3>
              <p className="text-2xl font-bold">{totalDeals} Deals</p>
            </div>
            <ChartLineUp size={24} className="text-muted-foreground" />
          </div>
          <div className="h-64 flex items-center">
            {sectorData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sectorData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    dataKey="count"
                    label={({ name, count }) => `${name}: ${count}`}
                  >
                    {sectorData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted-foreground text-center w-full">No sector data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-card border border-border p-6 rounded-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">Recent Activity</h3>
          <Pulse size={24} className="text-muted-foreground" />
        </div>
        <div className="space-y-3">
          {stats?.recent_activities?.length > 0 ? (
            stats.recent_activities.map((activity, index) => (
              <div 
                key={activity.id || index} 
                className="flex items-start gap-3 p-3 bg-muted/50 rounded-sm animate-fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <Lightning size={18} className="text-primary mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{activity.description}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {activity.user_name} • {formatDateTime(activity.created_at)}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-muted-foreground text-sm">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-card border border-border p-4 rounded-sm hover:-translate-y-[1px] transition-transform" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <div className="h-10 w-10 rounded-sm bg-primary/10 flex items-center justify-center">
          <Icon size={20} className="text-primary" />
        </div>
      </div>
    </div>
  );
}

function FioriTile({ color, icon: Icon, title, subtitle, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`fiori-tile fiori-tile-${color} text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white`}
      data-testid={`fiori-tile-${title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
    >
      <div>
        <Icon size={28} weight="duotone" />
      </div>
      <div>
        <p className="font-semibold text-base leading-tight">{title}</p>
        <p className="text-xs opacity-80 mt-0.5">{subtitle}</p>
      </div>
    </button>
  );
}

